// Refund.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  Modal, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Alert,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { useReactToPrint } from 'react-to-print';
import { format } from "date-fns";
import RefundSlipPrint from './components/RefundSlipPrint';
import { useAppContext } from './AppContext';

const Refund = ({ open, onClose, order }) => {
  const [refundAmount, setRefundAmount] = useState('');
  const [reason, setReason] = useState('');
  const [availableRefund, setAvailableRefund] = useState(0);
  const [error, setError] = useState('');
  const [refundDetails, setRefundDetails] = useState(null);
  const [refundHistory, setRefundHistory] = useState([]);
  const printRef = useRef();
  const [hasRefunded, setHasRefunded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { t } = useAppContext();

  useEffect(() => {
    const fetchRefundData = async () => {
      if (order) {
        const refundsQuery = query(
          collection(db, 'refunds'),
          where('orderId', '==', order.orderID)
        );
        const refundSnapshot = await getDocs(refundsQuery);
        
        const refunds = refundSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp.toDate()
        }));
        setRefundHistory(refunds);

        if (refunds.length > 0) {
          console.log(refunds.length);
          setHasRefunded(true);
          setError(t('This order has already been refunded. Multiple refunds are not allowed.'));
        }

        const totalRefunded = refunds.reduce((sum, refund) => 
          sum + Number(refund.amount), 0
        );
        
        const available = Number(order.paymentAmount) - totalRefunded;
        setAvailableRefund(available > 0 ? available : 0);
      }
    };
    fetchRefundData();
  }, [order, t]);

  const handleRefund = async () => {
    if (hasRefunded) {
      setError(t('This order has already been refunded. Multiple refunds are not allowed.'));
      return;
    }

    if (isProcessing) {
      return; // Prevent double submission
    }

    const amountToRefund = Number(refundAmount);
    if (!amountToRefund || amountToRefund <= 0 || amountToRefund > availableRefund) {
      setError(t('Refund amount must be between 0 and {max} MMK', { max: availableRefund.toLocaleString('my-MM') }));
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      const refundData = {
        refundId: Date.now().toString(),
        orderId: order.orderID,
        userId: order.userId || 'system',
        shopId: order.shopId,
        reason,
        amount: amountToRefund,
        timestamp: new Date()
      };

      const cashbookData = {
        amount: amountToRefund,
        timestamp: new Date(),
        shopId: order.shopId,
        orderId: order.orderID,
        description: `Refund for Order #${order.orderID}: ${reason}`,
        transactionType: 'refund',
        userId: order.userId || 'system',
        paymentMethod: 'Cash',
      };

      await addDoc(collection(db, 'refunds'), refundData);
      await addDoc(collection(db, 'cashbook'), cashbookData);

      // Set refundDetails for printing
      setRefundDetails({
        orderId: order.orderID,
        orderDate: order.orderDate,
        amount: amountToRefund,
        reason,
        timestamp: new Date()
      });

      setRefundHistory(prev => [...prev, { ...refundData, timestamp: new Date() }]);
      setAvailableRefund(prev => prev - amountToRefund);
      setHasRefunded(true);
      setError('');
      setRefundAmount('');
      setReason('');
    } catch (error) {
      setError(t('Error processing refund: {message}', { message: error.message }));
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Refund_Slip_${order?.orderID}`,
    onBeforeGetContent: () => {
      return new Promise((resolve) => {
        if (!refundDetails || !refundDetails.amount || refundDetails.amount <= 0) {
          setError(t('No valid refund details available for printing'));
          resolve(false);
        } else {
          resolve(true);
        }
      });
    },
    onPrintError: () => setError(t('Error printing refund slip')),
    onAfterPrint: () => {
      // Optionally clear refundDetails after printing
      // setRefundDetails(null);
    }
  });

  const handleRefundClose = (success) => {
    setRefundAmount('');
    setReason('');
    setError('');
    setRefundHistory([]);
    setRefundDetails(null);
    setIsProcessing(false);
    onClose(success);
    setHasRefunded(false);
  };

  return (
    <Modal open={open} onClose={() => handleRefundClose(false)}>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90%', sm: 600 },
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        borderRadius: 2,
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <Typography variant="h6" gutterBottom>
          {t('Refund Order')} #{order?.orderID}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {!error && (
          <Typography variant="subtitle1" gutterBottom>
            {t('Available Refund Amount')}: {availableRefund.toLocaleString('my-MM')} {t("MMK")}
          </Typography>
        )}
        
        <TextField
          label={t('Refund Amount')}
          type="number"
          fullWidth
          value={refundAmount}
          onChange={(e) => setRefundAmount(e.target.value)}
          sx={{ mb: 2 }}
          inputProps={{ min: 0, max: availableRefund }}
          error={refundAmount > availableRefund}
          helperText={refundAmount > availableRefund ? t('Maximum refund is {max} MMK', { max: availableRefund.toLocaleString('my-MM') }) : ''}
          disabled={hasRefunded}
        />

        <TextField
          label={t('Refund Reason')}
          multiline
          rows={3}
          fullWidth
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ mb: 2 }}
          disabled={hasRefunded}
        />

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          {t('Refund History')}
        </Typography>
        {refundHistory.length > 0 ? (
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('Date')}</TableCell>
                  <TableCell align="right">{t('Amount (MMK)')}</TableCell>
                  <TableCell>{t('Reason')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {refundHistory.map((refund) => (
                  <TableRow key={refund.refundId}>
                    <TableCell>
                      {format(new Date(refund.timestamp), "dd-MM-yyyy HH:mm a")}
                    </TableCell>
                    <TableCell align="right">
                      {refund.amount.toLocaleString('my-MM')}
                    </TableCell>
                    <TableCell>{refund.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" sx={{ mb: 3 }}>
            {t('No refund history available for this order.')}
          </Typography>
        )}

        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'flex-end'
        }}>
          <Button 
            onClick={handleRefund} 
            variant="contained" 
            color="secondary" 
            disabled={!refundAmount || !reason || hasRefunded || isProcessing}
          >
            {isProcessing ? t('Processing...') : t('Refund')}
          </Button>
          <Button 
            onClick={handlePrint} 
            variant="contained" 
            color="primary" 
            disabled={!refundDetails || !refundDetails.amount}
          >
            {t('Print')}
          </Button>
          <Button onClick={() => handleRefundClose(false)} variant="outlined">
            {t('Close')}
          </Button>
        </Box>
        
        <div style={{ display: 'none' }}>
          <RefundSlipPrint ref={printRef} refundDetails={refundDetails} />
        </div>
      </Box>
    </Modal>
  );
};

export default Refund;
